import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  GraduationCap, BookOpen, Trophy, Award, Loader2, RefreshCw,
  CheckCircle2, Circle, Link2, Upload, Trash2, Flame, Play, ExternalLink,
  Code2, Send, ChevronRight, ArrowLeft, MessageCircle, XCircle, Plus, Zap, Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import InteractiveLesson from '../components/InteractiveLesson';

export default function TrainHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Train');
  const [view, setView] = useState('classroom');
  const [tracks, setTracks] = useState([]);
  const [progress, setProgress] = useState(null);
  const [profile, setProfile] = useState(null);
  const [certBodies, setCertBodies] = useState([]);
  const [certs, setCerts] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  // Classroom state
  const [courses, setCourses] = useState([]);
  const [classProgress, setClassProgress] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [code, setCode] = useState('');
  const [grading, setGrading] = useState(false);
  const [gradeResult, setGradeResult] = useState(null);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [tutorMessages, setTutorMessages] = useState([]);
  const [tutorInput, setTutorInput] = useState('');
  const [tutorLoading, setTutorLoading] = useState(false);
  const [tutorSession] = useState(() => `tutor-${Date.now()}`);
  // Interactive Lab state
  const [interactiveLessons, setInteractiveLessons] = useState([]);
  const [selectedInteractive, setSelectedInteractive] = useState(null);
  const [generatingLesson, setGeneratingLesson] = useState(false);
  const [genTopic, setGenTopic] = useState('');
  const [genDifficulty, setGenDifficulty] = useState('beginner');
  const fetchData = useCallback(async () => {
    try {
      const [tracksRes, progressRes, profileRes, certsRes, challengeRes, bodiesRes, coursesRes, classProgressRes] = await Promise.all([
        api.get('/api/train/tracks'),
        api.get('/api/train/progress'),
        api.get('/api/train/profile'),
        api.get('/api/train/certifications'),
        api.get('/api/train/challenge'),
        api.get('/api/train/certification-bodies'),
        api.get('/api/train/classroom/courses'),
        api.get('/api/train/classroom/progress'),
      ]);
      setTracks(tracksRes.data.tracks || []);
      setProgress(progressRes.data);
      setProfile(profileRes.data);
      setCerts(certsRes.data.certifications || []);
      setChallenge(challengeRes.data);
      setCertBodies(bodiesRes.data.bodies || []);
      setCourses(coursesRes.data.courses || []);
      setClassProgress(classProgressRes.data);
    } catch (e) { console.error('TrainHub fetch error:', e); } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInteractiveLessons = useCallback(async () => {
    try {
      const { data } = await api.get('/api/train/lessons/interactive');
      setInteractiveLessons(data.lessons || []);
    } catch (e) { console.error('Interactive lessons error:', e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (view === 'lab') fetchInteractiveLessons(); }, [view, fetchInteractiveLessons]);

  const generateNewLesson = async () => {
    if (!genTopic.trim()) return;
    setGeneratingLesson(true);
    try {
      const { data } = await api.post('/api/train/lessons/generate', { topic: genTopic, difficulty: genDifficulty, language: 'python' });
      if (data.id) {
        toast.success(`Lesson "${data.title}" generated!`);
        setGenTopic('');
        fetchInteractiveLessons();
        setSelectedInteractive(data.id);
      } else {
        toast.error('Generation failed — try again');
      }
    } catch { toast.error('Lesson generation failed'); }
    setGeneratingLesson(false);
  };

  const markComplete = async (trackId, moduleId) => {
    await api.post('/api/train/progress', { track_id: trackId, module_id: moduleId, completed: true });
    const { data } = await api.get('/api/train/progress');
    setProgress(data);
    toast.success('Module completed!');
  };

  const isModuleComplete = (trackId, moduleId) => {
    return (progress?.progress || []).some(p => p.track_id === trackId && p.module_id === moduleId && p.completed);
  };

  const getTrackProgress = (track) => {
    const done = track.modules.filter(m => isModuleComplete(track.id, m.id)).length;
    return Math.round((done / track.modules.length) * 100);
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  // Lesson View (full-screen split pane)
  if (view === 'classroom' && selectedLesson && selectedCourse) {
    const exercises = [];
    if (lessonContent?.content) {
      const jsonMatch = lessonContent.content.match(/```json\s*([\s\S]*?)```/);
      if (jsonMatch) { try { exercises.push(...JSON.parse(jsonMatch[1])); } catch (e) { /* handled */ } }
    }

    const submitCode = async (exercise) => {
      if (!code.trim()) { toast.error('Write some code first'); return; }
      setGrading(true);
      try {
        const { data } = await api.post('/api/train/classroom/grade', {
          code, language: selectedCourse.language, exercise_title: exercise?.title || '',
          exercise_description: exercise?.description || '', expected_output: exercise?.expected_output || '',
        });
        setGradeResult(data);
        toast[data.passed ? 'success' : 'warning'](data.passed ? 'Passed!' : 'Check the feedback');
      } catch { toast.error('Grading failed'); }
      finally { setGrading(false); }
    };

    const sendTutor = async () => {
      if (!tutorInput.trim() || tutorLoading) return;
      const q = tutorInput.trim();
      setTutorInput('');
      setTutorMessages(prev => [...prev, { role: 'user', text: q }]);
      setTutorLoading(true);
      try {
        const { data } = await api.post('/api/train/classroom/tutor', {
          question: q, language: selectedCourse.language, lesson_context: selectedLesson.title, code, session_id: tutorSession,
        });
        setTutorMessages(prev => [...prev, { role: 'assistant', text: data.answer }]);
      } catch { setTutorMessages(prev => [...prev, { role: 'assistant', text: 'Tutor unavailable.' }]); }
      finally { setTutorLoading(false); }
    };

    const LANG_COLORS = { python: 'bg-blue-100 text-blue-700', javascript: 'bg-yellow-100 text-yellow-700', typescript: 'bg-blue-100 text-blue-700', java: 'bg-red-100 text-red-700', go: 'bg-cyan-100 text-cyan-700', rust: 'bg-orange-100 text-orange-700', sql: 'bg-emerald-100 text-emerald-700', react: 'bg-sky-100 text-sky-700', html_css: 'bg-orange-100 text-orange-700', bash: 'bg-gray-200 text-gray-700' };

    return (
      <div className="flex min-h-screen bg-gray-50" data-testid="classroom-lesson-view">
        <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => { setSelectedLesson(null); setLessonContent(null); setTutorMessages([]); }} className="text-xs rounded-lg" data-testid="classroom-back"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back</Button>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${LANG_COLORS[selectedCourse.language] || 'bg-gray-100 text-gray-600'}`}>{selectedCourse.language}</span>
              <h2 className="text-sm font-bold text-gray-900">{selectedLesson.title}</h2>
            </div>
            <Button variant="outline" size="sm" onClick={() => setTutorOpen(!tutorOpen)} className={`text-xs rounded-lg ${tutorOpen ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : ''}`} data-testid="classroom-tutor-toggle">
              <MessageCircle className="w-3.5 h-3.5 mr-1" /> AI Tutor
            </Button>
          </div>
          <div className="flex-1 flex overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
              {loadingLesson ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /><span className="ml-2 text-sm text-gray-400">Generating lesson...</span></div>
              ) : lessonContent?.content ? (
                <div className="prose prose-sm max-w-none" style={{ whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: '1.7' }} data-testid="classroom-lesson-content">
                  {lessonContent.content.replace(/```json[\s\S]*?```/, '')}
                </div>
              ) : null}
              {exercises.length > 0 && (
                <div className="mt-8 space-y-4" data-testid="classroom-exercises">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2"><Play className="w-4 h-4 text-[#D4A843]" /> Coding Exercises</h3>
                  {exercises.map((ex, i) => (
                    <div key={ex.id || i} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`classroom-exercise-${i}`}>
                      <h4 className="text-sm font-bold text-gray-900 mb-1">{ex.title}</h4>
                      <p className="text-xs text-gray-500 mb-3">{ex.description}</p>
                      {ex.expected_output && <p className="text-[10px] text-gray-400 mb-2">Expected: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{ex.expected_output}</code></p>}
                      <textarea value={code} onChange={e => setCode(e.target.value)} className="w-full h-40 rounded-lg border border-gray-200 p-4 font-mono text-sm bg-[#0F172A] text-emerald-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#D4A843]" placeholder={ex.starter_code || `// Write your ${selectedCourse.language} code here...`} spellCheck={false} data-testid={`classroom-editor-${i}`} />
                      <div className="flex items-center gap-2 mt-3">
                        <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => submitCode(ex)} disabled={grading} data-testid={`classroom-submit-${i}`}>
                          {grading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />} {grading ? 'Grading...' : 'Submit & Grade'}
                        </Button>
                        {ex.starter_code && <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setCode(ex.starter_code)}>Load Starter</Button>}
                      </div>
                      {gradeResult && (
                        <div className={`mt-3 border rounded-lg p-4 ${gradeResult.passed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="classroom-grade-result">
                          <div className="flex items-center gap-2 mb-2">
                            {gradeResult.passed ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                            <span className={`text-sm font-bold ${gradeResult.passed ? 'text-emerald-700' : 'text-red-700'}`}>{gradeResult.passed ? 'Passed!' : 'Try Again'}</span>
                            <span className="text-xs text-gray-500">Score: {gradeResult.score}/100</span>
                          </div>
                          <p className="text-xs text-gray-700">{gradeResult.feedback}</p>
                          {gradeResult.suggestions?.length > 0 && <div className="mt-2">{gradeResult.suggestions.map((s, j) => <p key={`j-${j}`} className="text-[10px] text-emerald-600">- {s}</p>)}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {tutorOpen && (
              <div className="w-80 border-l border-gray-200 bg-white flex flex-col shrink-0" data-testid="classroom-tutor-panel">
                <div className="bg-[#D4A843] text-[#0A0A0A] font-semibold px-4 py-3"><p className="text-sm font-bold">AI Tutor</p></div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {tutorMessages.length === 0 && <p className="text-xs text-gray-400 text-center py-8">Ask me anything!</p>}
                  {tutorMessages.map((m, i) => (
                    <div key={`item-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[90%] px-3 py-2 rounded-lg text-xs ${m.role === 'user' ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-800'}`} style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
                    </div>
                  ))}
                  {tutorLoading && <div className="flex justify-start"><div className="px-3 py-2 rounded-lg bg-gray-100"><Loader2 className="w-3 h-3 animate-spin text-gray-400" /></div></div>}
                </div>
                <div className="border-t border-gray-200 p-2 flex gap-2">
                  <input value={tutorInput} onChange={e => setTutorInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendTutor()} placeholder="Ask..." className="flex-1 text-xs px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4A843]" data-testid="tutor-input" />
                  <button onClick={sendTutor} className="bg-[#D4A843] text-[#0A0A0A] font-semibold p-2 rounded-lg" data-testid="tutor-send"><Send className="w-3 h-3" /></button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  const levelLabel = progress?.level === 3 ? 'Engineer' : progress?.level === 2 ? 'Builder' : 'User';
  const levelColor = progress?.level === 3 ? 'text-[#D4A843]' : progress?.level === 2 ? 'text-emerald-600' : 'text-amber-600';

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="train-hub">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                <GraduationCap className="w-6 h-6 text-[#D4A843]" /> Learn AI Coding
              </h1>
              <p className="text-sm text-gray-500 mt-1">Structured learning tracks, certifications, and professional profile</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-lg text-xs font-bold ${levelColor} bg-current/10`} style={{ backgroundColor: `${levelColor === 'text-[#D4A843]' ? 'rgba(0,47,167,0.1)' : levelColor === 'text-emerald-600' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)'}` }} data-testid="train-level">
                Level {progress?.level}: {levelLabel}
              </span>
              <span className="text-xs text-gray-400">{progress?.completed_modules || 0}/{progress?.total_modules || 0} modules</span>
            </div>
          </div>

          {/* Sub-nav */}
          <div className="flex gap-1 border-b border-gray-200 pb-0">
            {[{k:'classroom',l:'Classroom'},{k:'lab',l:'Interactive Lab'},{k:'tracks',l:'Learning Tracks'},{k:'profile',l:'Professional Profile'},{k:'certs',l:'Certifications'},{k:'challenge',l:'30-Day Challenge'}].map(t => (
              <button key={t.k} onClick={() => { setView(t.k); setSelectedTrack(null); setSelectedCourse(null); setSelectedLesson(null); setLessonContent(null); setSelectedInteractive(null); }} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${view === t.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`train-tab-${t.k}`}>{t.l}</button>
            ))}
          </div>

          {/* AI Coding Classroom */}
          {view === 'classroom' && !selectedCourse && !selectedLesson && (
            <ClassroomGrid courses={courses} classProgress={classProgress} onSelectCourse={setSelectedCourse} />
          )}

          {view === 'classroom' && selectedCourse && !selectedLesson && (
            <CourseDetail course={selectedCourse} onBack={() => setSelectedCourse(null)} onSelectLesson={(lesson) => {
              setSelectedLesson(lesson);
              setLessonContent(null);
              setCode('');
              setGradeResult(null);
              setLoadingLesson(true);
              api.post('/api/train/classroom/lesson', { course_id: selectedCourse.id, lesson_id: lesson.id })
                .then(({ data }) => setLessonContent(data))
                .catch(() => toast.error('Failed to load lesson'))
                .finally(() => setLoadingLesson(false));
            }} />
          )}

          {/* Learning Tracks */}
          {/* Interactive Lab */}
          {view === 'lab' && !selectedInteractive && (
            <div className="space-y-4" data-testid="interactive-lab">
              {/* AI Lesson Generator */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-[#D4A843]" /> AI Lesson Generator
                </h3>
                <p className="text-[11px] text-gray-400 mb-3">Describe a topic and AI creates a full interactive lesson with concepts, code examples, and graded exercises.</p>
                <div className="flex gap-2">
                  <Input value={genTopic} onChange={e => setGenTopic(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && genTopic.trim()) { generateNewLesson(); } }} placeholder="e.g., Variables & Data Types, Working with APIs, List Comprehensions..." className="flex-1 rounded-lg text-xs" disabled={generatingLesson} data-testid="gen-topic" />
                  <select value={genDifficulty} onChange={e => setGenDifficulty(e.target.value)} className="h-9 rounded-lg border border-gray-200 px-2 text-xs bg-white" data-testid="gen-difficulty">
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                  <Button onClick={generateNewLesson} disabled={generatingLesson || !genTopic.trim()} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="gen-btn">
                    {generatingLesson ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                    {generatingLesson ? 'Generating...' : 'Generate Lesson'}
                  </Button>
                </div>
              </div>

              {/* Lessons Grid */}
              {interactiveLessons.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {interactiveLessons.map(lesson => (
                    <div key={lesson.id} onClick={() => setSelectedInteractive(lesson.id)} className="bg-white border border-gray-200 rounded-lg p-5 cursor-pointer hover:border-[#D4A843] transition-all hover-lift" data-testid={`lesson-card-${lesson.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-[#D4A843]" />
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${lesson.difficulty === 'beginner' ? 'bg-emerald-100 text-emerald-700' : lesson.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{lesson.difficulty}</span>
                        <span className="text-[9px] text-gray-400">{lesson.language}</span>
                        {lesson.ai_generated && <span className="text-[9px] bg-[#D4A843]/10 text-[#D4A843] px-1 py-0.5 rounded">AI</span>}
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 line-clamp-2">{lesson.title}</h4>
                      <p className="text-[10px] text-gray-400 mt-1">{lesson.topic}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{new Date(lesson.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <Code2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No interactive lessons yet. Generate one with AI above!</p>
                </div>
              )}
            </div>
          )}

          {/* Interactive Lesson Viewer */}
          {view === 'lab' && selectedInteractive && (
            <InteractiveLesson lessonId={selectedInteractive} onBack={() => { setSelectedInteractive(null); fetchInteractiveLessons(); }} />
          )}

          {view === 'tracks' && !selectedTrack && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="train-tracks-grid">
              {tracks.map(track => {
                const pct = getTrackProgress(track);
                return (
                  <div key={track.id} className="bg-white border border-gray-200 rounded-lg p-5 hover-lift cursor-pointer" onClick={() => setSelectedTrack(track)} data-testid={`train-track-${track.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-[#D4A843] bg-[#D4A843]/10 px-2 py-0.5 rounded">{track.category}</span>
                      <span className="text-[10px] text-gray-400">Level {track.level} | ~{track.estimated_hours}h</span>
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 mb-1">{track.title}</h3>
                    <p className="text-[10px] text-gray-500 mb-3">{track.description}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5"><div className="bg-[#D4A843] rounded-full h-1.5 transition-all" style={{ width: `${pct}%` }} /></div>
                      <span className="text-[10px] font-bold text-[#D4A843]">{pct}%</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2">{track.modules.length} modules</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Track Detail */}
          {view === 'tracks' && selectedTrack && (
            <div className="space-y-4" data-testid="train-track-detail">
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => setSelectedTrack(null)} className="text-xs rounded-lg">Back</Button>
                <h2 className="text-lg font-bold text-gray-900">{selectedTrack.title}</h2>
              </div>
              <div className="space-y-2">
                {selectedTrack.modules.map(mod => {
                  const done = isModuleComplete(selectedTrack.id, mod.id);
                  return (
                    <div key={mod.id} className={`bg-white border rounded-lg p-4 ${done ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200'}`} data-testid={`train-mod-${mod.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-gray-300" />}
                          <div>
                            <p className={`text-sm font-medium ${done ? 'text-emerald-700' : 'text-gray-900'}`}>{mod.title}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">{mod.content?.slice(0, 120)}...</p>
                          </div>
                        </div>
                        {!done && (
                          <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={(e) => { e.stopPropagation(); markComplete(selectedTrack.id, mod.id); }} data-testid={`train-complete-${mod.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Professional Profile */}
          {view === 'profile' && (
            <ProfileEditor profile={profile} onSave={async (data) => {
              await api.put('/api/train/profile', data);
              const { data: p } = await api.get('/api/train/profile');
              setProfile(p);
              toast.success('Profile updated');
            }} />
          )}

          {/* Certifications */}
          {view === 'certs' && (
            <CertificationsManager certs={certs} bodies={certBodies} onAdd={async (data) => {
              await api.post('/api/train/certifications', data);
              const { data: c } = await api.get('/api/train/certifications');
              setCerts(c.certifications || []);
              toast.success('Certification added');
            }} onDelete={async (id) => {
              await api.delete(`/api/train/certifications/${id}`);
              setCerts(prev => prev.filter(c => c.id !== id));
              toast.success('Removed');
            }} />
          )}

          {/* 30-Day Challenge */}
          {view === 'challenge' && (
            <ChallengeView challenge={challenge} onStart={async () => {
              const { data } = await api.post('/api/train/challenge/start');
              setChallenge(data);
              toast.success('Challenge started!');
            }} onCheckin={async (week) => {
              const { data } = await api.put('/api/train/challenge/checkin', { week });
              setChallenge(data);
              toast.success(`Week ${week} completed!`);
            }} />
          )}
        </div>
      </main>
    </div>
  );
}

function ProfileEditor({ profile, onSave }) {
  const [form, setForm] = useState({
    linkedin_url: profile?.linkedin_url || '',
    indeed_url: profile?.indeed_url || '',
    usajobs_url: profile?.usajobs_url || '',
    github_url: profile?.github_url || '',
    portfolio_url: profile?.portfolio_url || '',
    bio: profile?.bio || '',
    skills: (profile?.skills || []).join(', '),
  });

  return (
    <div className="space-y-4 max-w-2xl" data-testid="train-profile-editor">
      <h2 className="text-lg font-bold text-gray-900">Professional Profile</h2>
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        {[
          { key: 'linkedin_url', label: 'LinkedIn Profile', icon: Link2, placeholder: 'https://linkedin.com/in/...' },
          { key: 'indeed_url', label: 'Indeed Profile', icon: Link2, placeholder: 'https://indeed.com/...' },
          { key: 'usajobs_url', label: 'USAJobs Profile', icon: Link2, placeholder: 'https://usajobs.gov/...' },
          { key: 'github_url', label: 'GitHub Profile', icon: Link2, placeholder: 'https://github.com/...' },
          { key: 'portfolio_url', label: 'Portfolio Website', icon: ExternalLink, placeholder: 'https://...' },
        ].map(f => (
          <div key={f.key}>
            <label className="text-xs font-medium text-gray-700 flex items-center gap-1"><f.icon className="w-3 h-3" /> {f.label}</label>
            <Input value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" placeholder={f.placeholder} data-testid={`train-${f.key}`} />
          </div>
        ))}
        <div>
          <label className="text-xs font-medium text-gray-700">Skills (comma-separated)</label>
          <Input value={form.skills} onChange={e => setForm({...form, skills: e.target.value})} className="mt-1 rounded-lg" placeholder="Python, React, Cybersecurity, AWS..." data-testid="train-skills" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700">Bio</label>
          <textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="w-full mt-1 h-20 rounded-lg border border-gray-200 p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#D4A843]" placeholder="Tell employers about your expertise..." data-testid="train-bio" />
        </div>
        <Button className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => onSave({ ...form, skills: form.skills.split(',').map(s => s.trim()).filter(Boolean) })} data-testid="train-save-profile">Save Profile</Button>
      </div>
    </div>
  );
}

function CertificationsManager({ certs, bodies, onAdd, onDelete }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ body_id: '', body_name: '', cert_name: '', credential_id: '', issue_date: '', expiry_date: '', verification_url: '' });
  const [selectedBody, setSelectedBody] = useState(null);

  const handleBodyChange = (bodyId) => {
    const b = bodies.find(x => x.id === bodyId);
    setSelectedBody(b);
    setForm({ ...form, body_id: bodyId, body_name: b?.name || '', cert_name: '' });
  };

  return (
    <div className="space-y-4" data-testid="train-certs-manager">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Certifications</h2>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="train-add-cert-btn">
          <Award className="w-3.5 h-3.5 mr-1" /> {showAdd ? 'Cancel' : 'Add Certification'}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3 animate-fade-in" data-testid="train-cert-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Certification Body *</label>
              <select value={form.body_id} onChange={e => handleBodyChange(e.target.value)} className="w-full mt-1 h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="train-cert-body">
                <option value="">Select body...</option>
                {bodies.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Certification *</label>
              <select value={form.cert_name} onChange={e => setForm({...form, cert_name: e.target.value})} className="w-full mt-1 h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="train-cert-name">
                <option value="">Select cert...</option>
                {(selectedBody?.certs || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-gray-700">Credential ID</label><Input value={form.credential_id} onChange={e => setForm({...form, credential_id: e.target.value})} className="mt-1 rounded-lg text-xs font-mono" placeholder="CERT-XXXXX" data-testid="train-cert-id" /></div>
            <div><label className="text-xs font-medium text-gray-700">Issue Date</label><Input type="date" value={form.issue_date} onChange={e => setForm({...form, issue_date: e.target.value})} className="mt-1 rounded-lg text-xs" data-testid="train-cert-issue" /></div>
            <div><label className="text-xs font-medium text-gray-700">Expiry Date</label><Input type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} className="mt-1 rounded-lg text-xs" data-testid="train-cert-expiry" /></div>
            <div><label className="text-xs font-medium text-gray-700">Verification URL</label><Input value={form.verification_url} onChange={e => setForm({...form, verification_url: e.target.value})} className="mt-1 rounded-lg text-xs font-mono" placeholder="https://verify..." data-testid="train-cert-url" /></div>
          </div>
          <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => { if (!form.body_id || !form.cert_name) { toast.error('Select body and cert'); return; } onAdd(form); setForm({ body_id: '', body_name: '', cert_name: '', credential_id: '', issue_date: '', expiry_date: '', verification_url: '' }); setShowAdd(false); }} data-testid="train-cert-save">Add Certification</Button>
        </div>
      )}

      <div className="space-y-2">
        {certs.map(c => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-testid={`train-cert-${c.id}`}>
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-[#D4A843]" />
              <div>
                <p className="text-sm font-bold text-gray-900">{c.cert_name}</p>
                <p className="text-[10px] text-gray-500">{c.body_name} {c.credential_id ? `| ${c.credential_id}` : ''}</p>
                {c.issue_date && <p className="text-[10px] text-gray-400">Issued: {c.issue_date} {c.expiry_date ? `| Expires: ${c.expiry_date}` : ''}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${c.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{c.status === 'verified' ? 'Verified' : 'Pending'}</span>
              <Button size="sm" variant="ghost" className="text-red-400 h-7 w-7 p-0" onClick={() => onDelete(c.id)} data-testid={`train-cert-del-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {certs.length === 0 && <p className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">No certifications added yet.</p>}
      </div>
    </div>
  );
}

function ChallengeView({ challenge, onStart, onCheckin }) {
  if (!challenge?.active) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200" data-testid="train-challenge-start">
        <Flame className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">30-Day AI Coding Challenge</h2>
        <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">Week 1: Python + API. Week 2: Memory + UI. Week 3: Database. Week 4: Launch something public.</p>
        <Button className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" onClick={onStart} data-testid="train-start-challenge">
          <Play className="w-4 h-4 mr-2" /> Start Challenge
        </Button>
      </div>
    );
  }

  const weeks = [
    { num: 1, ...challenge.week_1 },
    { num: 2, ...challenge.week_2 },
    { num: 3, ...challenge.week_3 },
    { num: 4, ...challenge.week_4 },
  ];

  return (
    <div className="space-y-4" data-testid="train-challenge-active">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Flame className="w-5 h-5 text-amber-500" /> 30-Day Challenge</h2>
          <span className="text-xs font-bold text-[#D4A843]">{challenge.day_streak} day streak</span>
        </div>
        <div className="space-y-3">
          {weeks.map(w => (
            <div key={w.num} className={`border rounded-lg p-4 flex items-center justify-between ${w.completed ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200'}`} data-testid={`train-week-${w.num}`}>
              <div className="flex items-center gap-3">
                {w.completed ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-gray-300" />}
                <div>
                  <p className={`text-sm font-medium ${w.completed ? 'text-emerald-700' : 'text-gray-900'}`}>Week {w.num}</p>
                  <p className="text-[10px] text-gray-500">{w.goal}</p>
                </div>
              </div>
              {!w.completed && (
                <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => onCheckin(w.num)} data-testid={`train-checkin-${w.num}`}>Complete</Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


const LANG_COLORS_GRID = { python: 'bg-blue-100 text-blue-700', javascript: 'bg-yellow-100 text-yellow-700', typescript: 'bg-blue-100 text-blue-700', java: 'bg-red-100 text-red-700', go: 'bg-cyan-100 text-cyan-700', rust: 'bg-orange-100 text-orange-700', sql: 'bg-emerald-100 text-emerald-700', react: 'bg-sky-100 text-sky-700', html_css: 'bg-orange-100 text-orange-700', bash: 'bg-gray-200 text-gray-700', csharp: 'bg-purple-100 text-purple-700', cpp: 'bg-indigo-100 text-indigo-700' };
const DIFF_BADGE = { beginner: 'bg-emerald-100 text-emerald-700', intermediate: 'bg-amber-100 text-amber-700', advanced: 'bg-red-100 text-red-700' };

function ClassroomGrid({ courses, classProgress, onSelectCourse }) {
  return (
    <div className="space-y-4" data-testid="classroom-section">
      {classProgress && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift"><p className="text-xl font-bold text-gray-900">{classProgress.total_courses}</p><p className="text-[10px] text-gray-500">Courses</p></div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift"><p className="text-xl font-bold text-emerald-600">{classProgress.passed_exercises}</p><p className="text-[10px] text-emerald-500">Exercises Passed</p></div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift"><p className="text-xl font-bold text-[#D4A843]">{classProgress.total_exercises}</p><p className="text-[10px] text-[#D4A843]">Submissions</p></div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift"><p className="text-xl font-bold text-amber-500">{classProgress.avg_score}</p><p className="text-[10px] text-amber-500">Avg Score</p></div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="classroom-course-grid">
        {courses.map(course => (
          <div key={course.id} className="bg-white border border-gray-200 rounded-lg p-5 hover-lift cursor-pointer group" onClick={() => onSelectCourse(course)} data-testid={`classroom-course-${course.id}`}>
            <div className="flex items-center justify-between mb-3">
              <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase ${LANG_COLORS_GRID[course.language] || 'bg-gray-100 text-gray-600'}`}>{course.language}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${DIFF_BADGE[course.difficulty]}`}>{course.difficulty}</span>
            </div>
            <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-[#D4A843] transition-colors">{course.title}</h3>
            <p className="text-[10px] text-gray-500 mb-3">{course.description}</p>
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <span className="text-[10px] text-gray-400">{course.lessons.length} lessons | ~{course.estimated_hours}h</span>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#D4A843]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CourseDetail({ course, onBack, onSelectLesson }) {
  const LANG_COLORS = LANG_COLORS_GRID;
  return (
    <div className="space-y-4" data-testid="classroom-course-view">
      <Button variant="outline" size="sm" onClick={onBack} className="text-xs rounded-lg"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> All Courses</Button>
      <div className="flex items-center gap-3">
        <span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase ${LANG_COLORS[course.language] || 'bg-gray-100 text-gray-600'}`}>{course.language}</span>
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${DIFF_BADGE[course.difficulty]}`}>{course.difficulty}</span>
      </div>
      <h2 className="text-xl font-bold text-gray-900">{course.title}</h2>
      <p className="text-sm text-gray-500">{course.description} | ~{course.estimated_hours}h</p>
      <div className="space-y-2" data-testid="classroom-lesson-list">
        {course.lessons.map(lesson => (
          <div key={lesson.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover-lift cursor-pointer" onClick={() => onSelectLesson(lesson)} data-testid={`classroom-lesson-${lesson.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">{lesson.order}</div>
              <div><p className="text-sm font-bold text-gray-900">{lesson.title}</p><p className="text-[10px] text-gray-500">{lesson.topic}</p></div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        ))}
      </div>
    </div>
  );
}
